export function defaultMarginPercent() {
  return 15;
}

export const HARDWARE_UNIT_PRESETS = [
  { unit: "台", unit_en: "unit" },
  { unit: "ແພັກ", unit_en: "pack" },
  { unit: "ແກັດ", unit_en: "carton" },
  { unit: "license", unit_en: "license" },
  { unit: "set", unit_en: "set" },
  { unit: "pcs", unit_en: "pcs" },
];

const LEGACY_UNIT_MAP = {
  "1 pack": { unit: "ແພັກ", unit_en: "pack" },
  "1 carton": { unit: "ແກັດ", unit_en: "carton" },
  "1ແພັກ": { unit: "ແພັກ", unit_en: "pack" },
  "1ແກັດ": { unit: "ແກັດ", unit_en: "carton" },
};

export function resolveUnitFields(unit, unitEn) {
  const u = unit != null ? String(unit).trim() : "";
  const ue = unitEn != null ? String(unitEn).trim() : "";

  if (u && LEGACY_UNIT_MAP[u]) {
    return LEGACY_UNIT_MAP[u];
  }

  if (ue && LEGACY_UNIT_MAP[ue]) {
    return LEGACY_UNIT_MAP[ue];
  }

  return {
    unit: u || null,
    unit_en: ue || null,
  };
}

export function displayUnitForLocale(unit, unitEn, locale = "en") {
  const resolved = resolveUnitFields(unit, unitEn);

  if (locale === "en" || locale === "th") {
    return resolved.unit_en || resolved.unit || "";
  }

  return resolved.unit || resolved.unit_en || "";
}

export function findHardwareUnitPreset(value) {
  const trimmed = String(value || "").trim();
  if (!trimmed) {
    return null;
  }

  const legacy = LEGACY_UNIT_MAP[trimmed];
  if (legacy) {
    return legacy;
  }

  return (
    HARDWARE_UNIT_PRESETS.find(
      (preset) =>
        preset.unit === trimmed ||
        preset.unit_en === trimmed ||
        preset.unit.toLowerCase() === trimmed.toLowerCase() ||
        preset.unit_en.toLowerCase() === trimmed.toLowerCase(),
    ) || null
  );
}

export function calculatePosSmartPrice(cost, marginType, marginValue) {
  const baseCost = Number(cost) || 0;
  const margin = Number(marginValue) || 0;

  if (marginType === "fixed") {
    return Math.round((baseCost + margin) * 100) / 100;
  }

  return Math.round(baseCost * (1 + margin / 100) * 100) / 100;
}

export function productCost(item) {
  if (!item) return 0;
  return Number(item.cost != null ? item.cost : item.wholesale) || 0;
}

export function productPosSmartPrice(item) {
  if (!item) return 0;
  if (item.pos_smart_price != null) {
    return Number(item.pos_smart_price) || 0;
  }
  return calculatePosSmartPrice(
    productCost(item),
    item.margin_type || "percent",
    item.margin_value != null ? item.margin_value : defaultMarginPercent(),
  );
}

const API_BASE = (process.env.VUE_APP_BASE_URL || "").replace(/\/$/, "");

/**
 * Resolve hardware image URL for display (fixes legacy localhost URLs without API port).
 */
export function resolveHardwareImageUrl(url) {
  if (url == null || typeof url !== "string") {
    return null;
  }

  const trimmed = url.trim();
  if (!trimmed) {
    return null;
  }

  if (/^https?:\/\//i.test(trimmed)) {
    if (/amazonaws\.com/i.test(trimmed) || /devla\.s3/i.test(trimmed)) {
      return trimmed;
    }

    const legacyLocal = trimmed.match(
      /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/uploads\/hardware\/.+)$/i,
    );
    if (legacyLocal && API_BASE) {
      const port = legacyLocal[2] || "";
      if (!port || port === ":80") {
        return `${API_BASE}${legacyLocal[3]}`;
      }
    }

    return trimmed;
  }

  const path = trimmed.replace(/^\//, "");
  if (!API_BASE) {
    return trimmed.startsWith("uploads/") ? `/${path}` : `/uploads/${path}`;
  }

  if (path.startsWith("uploads/")) {
    return `${API_BASE}/${path}`;
  }

  return `${API_BASE}/uploads/${path}`;
}

export function resolveHardwareImageUrls(images) {
  if (!Array.isArray(images)) {
    return [];
  }

  return images
    .map((url) => resolveHardwareImageUrl(url))
    .filter((url) => typeof url === "string" && url.length > 0);
}

export function primaryProductImage(item) {
  if (!item?.images?.length) {
    return null;
  }

  const first = item.images[0];
  if (typeof first !== "string" || !first.trim()) {
    return null;
  }

  return resolveHardwareImageUrl(first.trim());
}

/** Stored line image, or live product catalog image when detail was saved before upload. */
export function resolveSolutionLineImageUrl(line) {
  if (!line) {
    return null;
  }

  const stored = line.image_url;
  if (stored != null && String(stored).trim() !== "") {
    return resolveHardwareImageUrl(String(stored).trim());
  }

  return primaryProductImage(line.hardware_product);
}

/**
 * True when the browser must not load the URL directly (CORS or auth).
 * S3 buckets often omit Access-Control-Allow-Origin for SPA origins.
 */
export function hardwareImageNeedsProxy(url) {
  if (!url || typeof url !== "string") {
    return false;
  }

  if (/\/uploads\/hardware\//i.test(url)) {
    return true;
  }

  if (/amazonaws\.com/i.test(url) || /devla\.s3/i.test(url)) {
    return true;
  }

  if (typeof window !== "undefined" && /^https?:\/\//i.test(url)) {
    try {
      return new URL(url).origin !== window.location.origin;
    } catch (error) {
      return false;
    }
  }

  return false;
}

/**
 * Display URL for hardware images via same-origin API proxy when needed.
 * @param {import('axios').AxiosInstance} http
 */
export async function fetchHardwareImageDisplaySrc(http, url) {
  const resolved = resolveHardwareImageUrl(url);
  if (!resolved) {
    return null;
  }

  if (!hardwareImageNeedsProxy(resolved)) {
    return resolved;
  }

  try {
    const response = await http.get("/admin/hardware/image-proxy", {
      params: { url: resolved },
      responseType: "blob",
    });

    return URL.createObjectURL(response.data);
  } catch (error) {
    return null;
  }
}

export function localizeCatalogField(item, field, locale = "en") {
  if (!item) return "";
  const enKey = `${field}_en`;
  const primary = locale === "ch" ? field : enKey;
  const fallback = locale === "ch" ? enKey : field;
  const value = item[primary] || item[fallback];
  return value != null ? String(value).trim() : "";
}

export function displayHardwareCategory(item, t, te) {
  if (!item) return "";
  const slug = (item.category || item.id || "").replace(/-/g, "_");
  const key = `hardware_cat_${slug}`;
  if (slug && typeof te === "function" && te(key)) {
    return t(key);
  }
  return item.category_label || item.label || "";
}

export function buildQuoteLineFromProduct(item, localizeName) {
  const unitCost = productCost(item);
  const marginType = item.margin_type || "percent";
  const marginValue =
    item.margin_value != null
      ? Number(item.margin_value)
      : defaultMarginPercent();
  const unitPrice = productPosSmartPrice(item);
  const units = resolveUnitFields(item.unit, item.unit_en);

  return {
    hardware_product_id: item.id,
    source_type: item.source_type || "hardware",
    name: typeof localizeName === "function" ? localizeName(item, "name") : item.name,
    model: typeof localizeName === "function" ? localizeName(item, "model") : item.model,
    spec: typeof localizeName === "function" ? localizeName(item, "spec") : item.spec,
    unit: units.unit,
    unit_en: units.unit_en,
    unit_cost: unitCost,
    margin_type: marginType,
    margin_value: marginValue,
    unit_price: unitPrice,
    quantity: 1,
    months: 1,
  };
}
